import React, { useLayoutEffect, useRef, useState } from "react";
import "./ChartFrame.scss";

/**
 * Glass-style floating tooltip for SVG charts.
 *
 * `x`/`y` are the hovered point's pixel coordinates inside the `.cb-chart-frame`.
 * The frame clips overflow, so the tooltip is placed edge-aware: drawn above the
 * point by default, flipped below when there isn't room above (point near the
 * top), and clamped horizontally so it is never cropped at the side edges.
 */
export default function ChartTooltip({ x, y, visible, children }) {
  const ref = useRef(null);
  // `ready` gates visibility until the box has been measured and placed, so the
  // first frame is never shown in the wrong (clipped) position.
  const [place, setPlace] = useState({ below: false, left: x, ready: false });

  useLayoutEffect(() => {
    if (!visible) {
      setPlace((p) => (p.ready ? { below: false, left: x, ready: false } : p));
      return;
    }
    const el = ref.current;
    const frame = el && el.closest(".cb-chart-frame");
    if (!el || !frame) {
      // No positioned frame found: fall back to the default above-placement.
      setPlace({ below: false, left: x, ready: true });
      return;
    }
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    const frameW = frame.clientWidth;
    const margin = 10; // matches the 10px gap baked into the CSS transform
    const pad = 6; // small gutter kept from the frame edges

    // Flip below when the box, drawn above the point, would cross the top edge.
    const below = y - h - margin < 0;
    // Clamp the horizontally-centered box so neither edge leaves the frame.
    const half = w / 2;
    const left = Math.min(Math.max(x, half + pad), Math.max(frameW - half - pad, half + pad));

    setPlace({ below, left, ready: true });
  }, [visible, x, y, children]);

  if (!visible) return null;

  return (
    <div
      ref={ref}
      className={"cb-chart-tooltip" + (place.below ? " cb-chart-tooltip--below" : "")}
      style={{ left: place.left, top: y, visibility: place.ready ? "visible" : "hidden" }}
      role="tooltip"
    >
      {children}
    </div>
  );
}
